import React, { useState, useEffect, useRef } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from '../utils/googleMaps';

interface AddressComponents {
  street_number?: string;
  route?: string;
  locality?: string;
  administrative_area_level_1?: string;
  country?: string;
  postal_code?: string;
  formatted_address?: string;
  lat?: number;
  lng?: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onAddressSelect?: (components: AddressComponents) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Start typing an address...',
  className = '',
  required = false
}) => {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load Google Maps API on component mount
  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        console.log('Loading Google Maps API...');
        await loadGoogleMapsAPI();
        console.log('Google Maps API loaded successfully');
        setMapsLoaded(true);
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    if (!isGoogleMapsLoaded()) {
      initGoogleMaps();
    } else {
      setMapsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (mapsLoaded && window.google && !autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      
      // Create a hidden map div for PlacesService
      if (!mapDiv.current) {
        mapDiv.current = document.createElement('div');
        const map = new window.google.maps.Map(mapDiv.current);
        placesService.current = new window.google.maps.places.PlacesService(map);
      }
    }
  }, [mapsLoaded]);

  const fetchSuggestions = (input: string) => {
    if (!mapsLoaded || !autocompleteService.current || input.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ['address'],
      },
      (predictions, status) => {
        setIsLoading(false);
        console.log('Autocomplete status:', status, 'Predictions:', predictions);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          console.error('Autocomplete failed:', status);
          setSuggestions([]);
        }
      }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the API call
    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const getPlaceDetails = (placeId: string) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const components: AddressComponents = {
            formatted_address: place.formatted_address,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng()
          };

          // Parse address components
          place.address_components?.forEach(component => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              components.street_number = component.long_name;
            }
            if (types.includes('route')) {
              components.route = component.long_name;
            }
            if (types.includes('locality')) {
              components.locality = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              components.administrative_area_level_1 = component.short_name;
            }
            if (types.includes('country')) {
              components.country = component.long_name;
            }
            if (types.includes('postal_code')) {
              components.postal_code = component.long_name;
            }
          });

          if (onAddressSelect) {
            onAddressSelect(components);
          }
        }
      }
    );
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    onChange(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Get detailed place information
    getPlaceDetails(suggestion.place_id);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click events to fire
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
          required={required}
        />
        <MapPinIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        {isLoading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 dark:bg-gray-700 focus:outline-none"
            >
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {suggestion.structured_formatting.main_text}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {suggestion.structured_formatting.secondary_text}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;